import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  StandardizedTable,
  TableColumn,
  TableRow,
} from "@/components/ui/standardized-table";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Users,
  Plus,
  Edit,
  Trash2,
  Receipt,
  ArrowUpCircle,
  Loader2,
  CheckCircle,
  XCircle,
  Clock,
  Shield,
  User as UserIcon,
  Eye,
  EyeOff,
  AlertTriangle,
  Sparkles,
  Crown,
  Building2,
  Store,
  CalendarDays,
  FileText,
  Gift,
  CreditCard,
  Coins,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import {
  EXPLICACAO_DO_NIVEL,
  ROTULO_DO_NIVEL,
  niveisDe,
  nivelPadraoDe,
  normalizeAccessLevel,
  type AccessLevel,
  type AccessType,
} from "@/lib/access-level";
import {
  acessosDoPerfil,
  administraAConta,
  ehAssinante,
} from "@/lib/account-access-context";
import {
  CABECALHO_403_ESPERADO,
  tenantAtivo,
  type HeyApiError,
} from "@/lib/hey-api-client";
import { useAuth } from "@/contexts/auth.context";
import planService, {
  CATEGORIA_DO_CADASTRO,
  Plan,
  planosDoCadastro,
} from "@/services/plan.service";
import { useAccount } from "@/contexts/account.context";
import AccountSelector from "@/components/accounts/AccountSelector";
import AccountSelectionModal from "@/components/accounts/AccountSelectionModal";
import {
  apiV1TeamsGet,
  createAccountUserApiV1MyAccountUsersPost,
  deleteAccountUserApiV1MyAccountUsersAccountUserIdDelete,
  getInvoiceDetailApiV1MyAccountInvoicesInvoiceIdGet,
  getSubscriptionInfoApiV1MyAccountSubscriptionGet,
  listAccountUsersApiV1MyAccountUsersGet,
  listAccountsApiV1AccountsGet,
  listInvoicesApiV1MyAccountInvoicesGet,
  requestDataDeletionApiV1DataDeletionPost,
  updateAccountUserApiV1MyAccountUsersAccountUserIdPut,
} from "@/backend-sdk/client/sdk.gen";
import TeamMultiSelect, {
  type TeamMultiSelectOption,
} from "@/components/teams/TeamMultiSelect";
import { UpgradeDialog } from "@/components/pages/UpgradeDialog";
import { BuyAiCreditsDialog } from "@/components/ai-credits/BuyAiCreditsDialog";
import {
  fetchSaldoDeIa,
  type SaldoDeIa,
} from "@/services/ai-credit-packs.service";
import {
  fetchEligiblePlans,
  getUpgradeBlockedMessage,
} from "@/services/subscription.service";
import type {
  AccountUserCreate,
  AccountUserUpdate,
  EligiblePlanItem,
  TeamItem,
  MyAccountAvailablePlan,
  MyAccountCurrentPlan,
  MyAccountInvoiceDetailResponse,
  MyAccountInvoiceItem,
  MyAccountTrialInfo,
  MyAccountUserItem,
} from "@/backend-sdk/client/types.gen";

// Tipos
interface AccountUser {
  id: string;
  user_id: string;
  name: string;
  email: string;
  /**
   * O que a credencial pode fazer: `viewer` lê, `editor` lê e escreve, `admin` também
   * administra os usuários e os times do escopo. Substituiu o antigo `role`, que nomeava um
   * papel sem efeito sobre escrita nenhuma.
   */
  access_level: AccessLevel;
  status: "active" | "pending" | "inactive";
  created_at: string;
  last_login?: string | null;
  /**
   * De que lado do balcão a pessoa está: `team` é funcionário da agência, `client` é a
   * credencial cedida a quem é da loja. É o campo que decide se a edição mostra a lista de
   * times ou a de lojas — e é o mesmo que filtra os candidatos a membro de time.
   */
  access_type: AccessType;
  /** Os times de que o funcionário participa. Vazio para a credencial de cliente. */
  teams: { id: string; name: string }[];
  /** As lojas que a credencial de cliente enxerga. Vazio para o funcionário. */
  accounts: { account_id: string; name: string }[];
}

interface Invoice {
  id: string;
  month: string;
  year: number;
  amount: number;
  status: "paid" | "pending" | "overdue" | "cancelled";
  paid_at?: string | null;
  due_date?: string | null;
  description?: string;
  invoice_number?: string;
  billing_cycle?: string;
  discount_amount?: number;
  setup_fee?: number;
  monthly_amount?: number;
  coupon_code?: string;
}

interface InvoiceItem {
  description?: string;
  amount?: number;
  quantity?: number;
  type?: string;
  unit_price?: number;
}

interface InvoiceDetail extends Invoice {
  plan?: {
    name?: string;
    price?: number;
    features?: string[];
  };
  subscription_start?: string;
  created_at?: string;
  period_start?: string;
  period_end?: string;
  notes?: string;
  setup_fee?: number;
  discount_amount?: number;
  coupon_code?: string;
  monthly_amount?: number;
  items?: InvoiceItem[];
}

interface TrialInfo {
  is_in_trial: boolean;
  trial_end: string | null;
  trial_days_remaining: number;
  subscription_status: string;
  plan_name: string;
  plan_price: number;
}

interface CurrentPlan {
  id: string;
  name: string;
  price: number;
  features: string[];
  category: string;
  tier: string;
  status: string;
  trial_end?: string | null;
  period_end?: string | null;
}

/**
 * O seletor do que a credencial pode fazer — o **único** eixo de permissão da tela.
 *
 * Até esta versão havia dois campos aqui: uma "Função" (`admin`/`editor`/`viewer`) que não
 * decidia escrita nenhuma, e uma marca invisível de somente leitura que decidia. Agora é um
 * campo só, e ele significa o que diz.
 *
 * `admin` some para a credencial de cliente: administrar a equipe e os times é da agência, e
 * é a única combinação dos dois eixos que o servidor recusa. Esconder a opção evita oferecer
 * um `422`.
 */
const SeletorDeNivel = ({
  id,
  accessType,
  value,
  onChange,
}: {
  id: string;
  accessType: AccessType;
  value: AccessLevel;
  onChange: (nivel: AccessLevel) => void;
}) => {
  const niveis = niveisDe(accessType);

  return (
    <div className="space-y-2">
      <Label htmlFor={id}>Pode *</Label>
      <Select value={value} onValueChange={(v: AccessLevel) => onChange(v)}>
        <SelectTrigger id={id}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {niveis.map((nivel) => (
            <SelectItem key={nivel} value={nivel}>
              {ROTULO_DO_NIVEL[nivel]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <p className="text-xs text-muted-foreground">
        {EXPLICACAO_DO_NIVEL[value]}
      </p>
    </div>
  );
};

const normalizeStatus = (status: string): AccountUser["status"] => {
  if (status === "active" || status === "pending" || status === "inactive") {
    return status;
  }
  return "inactive";
};

const normalizeInvoiceStatus = (status: string): Invoice["status"] => {
  if (
    status === "paid" ||
    status === "pending" ||
    status === "overdue" ||
    status === "cancelled"
  ) {
    return status;
  }
  return "pending";
};

const featuresToList = (
  features:
    | MyAccountAvailablePlan["features"]
    | MyAccountCurrentPlan["features"],
): string[] =>
  Object.entries(features).flatMap(([key, value]) => {
    if (Array.isArray(value)) return value;
    if (value === null || value === false) return [];
    if (value === true) return [key];
    return [`${key}: ${String(value)}`];
  });

const mapAccountUser = (item: MyAccountUserItem): AccountUser => ({
  id: item.id,
  user_id: item.user_id,
  name: item.name,
  email: item.email,
  access_level: normalizeAccessLevel(item.access_level),
  status: normalizeStatus(item.status),
  created_at: item.created_at ?? "",
  last_login: item.last_login,
  access_type: item.access_type === "client" ? "client" : "team",
  teams: item.teams ?? [],
  accounts: item.accounts ?? [],
});

const mapInvoice = (invoice: MyAccountInvoiceItem): Invoice => ({
  id: invoice.id,
  month: invoice.month,
  year: invoice.year,
  amount: invoice.amount,
  status: normalizeInvoiceStatus(invoice.status),
  paid_at: invoice.paid_at,
  due_date: invoice.due_date,
  description: invoice.description ?? undefined,
  invoice_number: invoice.invoice_number,
  billing_cycle: invoice.billing_cycle,
  discount_amount: invoice.discount_amount,
  setup_fee: invoice.setup_fee,
  monthly_amount: invoice.monthly_amount,
  coupon_code: invoice.coupon_code ?? undefined,
});

const mapTrialInfo = (trialInfo: MyAccountTrialInfo): TrialInfo => ({
  is_in_trial: trialInfo.is_in_trial,
  trial_end: trialInfo.trial_end ?? null,
  trial_days_remaining: trialInfo.trial_days_remaining,
  subscription_status: trialInfo.subscription_status,
  plan_name: trialInfo.plan_name,
  plan_price: trialInfo.plan_price,
});

const mapCurrentPlan = (plan: MyAccountCurrentPlan): CurrentPlan => ({
  id: plan.id,
  name: plan.name,
  price: plan.price,
  features: featuresToList(plan.features),
  category: plan.category,
  tier: plan.tier,
  status: plan.status,
  trial_end: plan.trial_end,
  period_end: plan.period_end,
});

/**
 * A assinatura em `trial` só está em período grátis enquanto o `trial_end` não passou. Plano
 * sem dias grátis também nasce em `trial`, sem `trial_end`, e fica assim até o primeiro
 * pagamento.
 */
const emPeriodoGratis = (trialEnd: string | null | undefined): boolean =>
  !!trialEnd && new Date(trialEnd).getTime() > Date.now();

const mapInvoiceDetail = (
  detail: MyAccountInvoiceDetailResponse,
): InvoiceDetail => ({
  ...mapInvoice({
    ...detail,
    plan: detail.plan,
  }),
  billing_cycle: detail.billing_cycle,
  created_at: detail.created_at ?? undefined,
  period_start: detail.period_start ?? undefined,
  period_end: detail.period_end ?? undefined,
  notes: detail.notes ?? undefined,
  subscription_start: detail.subscription_start ?? undefined,
  plan: {
    name: detail.plan.name,
    price: detail.plan.price,
    features: featuresToList(detail.plan.features),
  },
  items: detail.items?.map((item) => ({
    description: item.description ?? undefined,
    amount: item.amount ?? undefined,
    quantity: item.quantity ?? undefined,
    type: item.type ?? undefined,
    unit_price: item.unit_price ?? undefined,
  })),
});

const isAccountUser = (row: TableRow): row is TableRow & AccountUser =>
  typeof row.id === "string" &&
  typeof row.user_id === "string" &&
  typeof row.name === "string" &&
  typeof row.email === "string" &&
  typeof row.access_level === "string" &&
  typeof row.status === "string" &&
  typeof row.created_at === "string" &&
  (row.access_type === "team" || row.access_type === "client") &&
  Array.isArray(row.teams) &&
  Array.isArray(row.accounts);

const isInvoice = (row: TableRow): row is TableRow & Invoice =>
  typeof row.id === "string" &&
  typeof row.month === "string" &&
  typeof row.year === "number" &&
  typeof row.amount === "number" &&
  typeof row.status === "string" &&
  (!("due_date" in row) ||
    typeof row.due_date === "string" ||
    row.due_date === null);

const getErrorMessage = (error: unknown): string => {
  if (error && typeof error === "object") {
    if ("message" in error && typeof error.message === "string") {
      return error.message;
    }
    if ("response" in error) {
      const response = error.response as {
        data?: { detail?: unknown };
      };
      if (typeof response.data?.detail === "string") {
        return response.data.detail;
      }
    }
  }
  return "Tente novamente.";
};

/**
 * O que a aba Usuários mostra a quem não administra a conta.
 *
 * <M-247> Antes ela mostrava, a essa pessoa, um cabeçalho de tabela sem linha nenhuma, um
 * catálogo dos três níveis de permissão que não são dela, e um toast vermelho de erro — para
 * descrever um acesso perfeitamente normal. A decisão de tela (2026-09-06) foi trocar isso
 * pelo que é verdade dela: **o acesso que ela tem**, com o mesmo rótulo que quem a
 * administra vê no lugar dela.
 *
 * O nível vem de `access_level`, e não de `role`. Os dois são publicados por `/users/me` e
 * divergem em dado real: <D-061> aposentou o eixo `roles`, que segue gravado em espelho e
 * não autoriza nada. Ver `acessosDoPerfil`.
 *
 * A lista vazia é alcançável — o perfil pode chegar antes do primeiro `GET /users/me`, ou
 * de um servidor anterior ao campo —, e nela sobra a frase, que é a parte que importa.
 */
const MeuAcesso = ({
  acessos,
}: {
  acessos: { accountId: string; accountName: string; level: AccessLevel }[];
}) => (
  <div className="space-y-4">
    {acessos.length > 0 && (
      <div className="space-y-3">
        {acessos.map((acesso) => (
          <div
            key={acesso.accountId}
            className="flex flex-col gap-1 p-4 border rounded-lg sm:flex-row sm:items-center sm:justify-between"
          >
            <div className="min-w-0">
              <p className="font-medium truncate">{acesso.accountName}</p>
              <p className="text-sm text-muted-foreground">
                {EXPLICACAO_DO_NIVEL[acesso.level]}
              </p>
            </div>
            <Badge variant="secondary" className="shrink-0 w-fit">
              {ROTULO_DO_NIVEL[acesso.level]}
            </Badge>
          </div>
        ))}
      </div>
    )}

    <p className="text-sm text-muted-foreground">
      Adicionar, editar ou remover pessoas é de quem administra a conta. Fale
      com o administrador dela para mudar o seu acesso.
    </p>
  </div>
);

const MyAccountPage = () => {
  const { user } = useAuth();
  const {
    isAgencyUser,
    canSwitchAccounts,
    selectedAccount,
    hasChosenMainAccount,
  } = useAccount();
  /**
   * Nem toda aba desta página é da conta selecionada.
   *
   * **Usuários** é da conta: a lista muda quando se troca de loja. **Faturas** e
   * **Assinatura** são do *assinante* — as quatro rotas de cobrança resolvem o perfil pelo
   * `sub` do token, e não pela loja ativa —, e por isso quem decide se elas existem é
   * `ehAssinante`, e não em que loja se está. Quem não tem perfil de assinante (o membro de
   * time, a credencial de cliente, o usuário convidado de uma loja) recebe
   * `404 Perfil de assinante não encontrado.` nas três cargas, e a página encheria a tela de
   * erro para descrever um acesso que é normal.
   *
   * <M-216> O gate era `isSubaccount`, e escondia as duas abas de quem estivesse dentro de
   * uma loja. Para a agência isso passava despercebido — ela tem "Conta Principal", um lugar
   * legítimo em que ficar sem loja selecionada, e via a própria cobrança de lá. O **lojista
   * com mais de uma conta** não tem esse lugar: desde M-214 ele é empurrado para dentro de
   * uma loja assim que entra, e o seletor dele não oferece Conta Principal — então as duas
   * abas sumiam para sempre, e ele não via a própria fatura nem o próprio plano em canto
   * nenhum do painel.
   *
   * O item previa reencaminhar as quatro chamadas pelo `heyAdminApiClient` (o token do
   * dono), porque o token da troca de conta era emitido **no nome** do usuário-dono da loja.
   * <D-062> tirou esse trabalho da frente: o `sub` do token de subconta é quem pediu a
   * troca, e as quatro rotas respondem `200` com a cobrança dele. Medido em 2026-09-06
   * contra a API local, com o token de subconta de um lojista de quatro lojas.
   */
  const semCobranca = !ehAssinante(user);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("users");
  const [showAccountModal, setShowAccountModal] = useState(false);

  /**
   * <M-280> A loja cujo saldo de IA o card mostra — a ativa.
   *
   * O saldo de IA é **por loja**: a franquia do plano e o que foi comprado em pacote. A Conta
   * Principal da agência não é loja, e nela o card não aparece; em qualquer loja ele aparece,
   * de agência ou não, e é por ele que o dono compra créditos para aquela loja.
   */
  const lojaDosCreditos =
    isAgencyUser && (hasChosenMainAccount || !selectedAccount)
      ? null
      : (selectedAccount?.customer_uuid ?? tenantAtivo() ?? null);
  const nomeDaLojaDosCreditos = selectedAccount?.name ?? null;
  const [aiCredits, setAiCredits] = useState<SaldoDeIa | null>(null);
  const [buyCreditsOpen, setBuyCreditsOpen] = useState(false);

  /**
   * Quem está olhando administra a equipe desta conta?
   *
   * <M-247> `undefined` é "o servidor não disse" — resposta anterior ao campo —, e nele o
   * plano B de `loadUsers` chama a rota e lê o `403`. `recusadaPeloServidor` é o que esse
   * plano B guarda, para que a tela desenhe a mesma coisa nos dois caminhos.
   *
   * Não confundir com `isCurrentUserAdmin`, mais abaixo: aquele é derivado da **lista** de
   * usuários, e por isso não serve de gate para a chamada que traz a lista.
   */
  const [recusadaPeloServidor, setRecusadaPeloServidor] = useState(false);
  const administraOEscopo = administraAConta(user);
  const podeGerirUsuarios =
    administraOEscopo === undefined ? !recusadaPeloServidor : administraOEscopo;

  /** O acesso da própria pessoa, para a tela dizer a ela o que ela é. */
  const meusAcessos = useMemo(() => acessosDoPerfil(user), [user]);

  // Estados para Usuários
  const [users, setUsers] = useState<AccountUser[]>([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [showAddUserDialog, setShowAddUserDialog] = useState(false);
  const [showEditUserDialog, setShowEditUserDialog] = useState(false);
  const [showDeleteUserDialog, setShowDeleteUserDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState<AccountUser | null>(null);
  const [userFormData, setUserFormData] = useState({
    name: "",
    email: "",
    password: "",
    accessLevel: "viewer" as AccessLevel,
    accessType: "team" as AccessType,
    teamIds: [] as string[],
    accountIds: [] as string[],
  });
  const [showEditPassword, setShowEditPassword] = useState(false);

  /**
   * O que os dois seletores do formulário oferecem: os times da agência e as lojas dela.
   *
   * As duas listas vêm com o **mesmo token** das rotas de usuário desta tela, e não com o
   * token principal. É o que mantém a oferta igual ao que o servidor aceita: quem entrou
   * numa loja (ou é dono de uma) administra só aquela conta, e `GET /api/v1/teams` responde
   * `403` para ele — a lista fica vazia em vez de oferecer times que o `POST` recusaria.
   *
   * A lista de lojas tem um plano B pelo mesmo motivo: `GET /api/v1/accounts` é do
   * assinante e responde `403 Usuário não é um assinante` para o dono de uma loja. As
   * contas do próprio perfil são o que resta, e são exatamente as que ele administra.
   */
  const [timesDaAgencia, setTimesDaAgencia] = useState<TeamItem[]>([]);
  const [lojasDaAgencia, setLojasDaAgencia] = useState<
    { id: string; name: string; hint?: string | null }[]
  >([]);
  const [escopoCarregando, setEscopoCarregando] = useState(false);

  /**
   * Se quem está olhando pode **gerenciar** a equipe da conta.
   *
   * Até 2026-08-26 a API só exigia papel `admin`/`owner` para trocar a senha de outra
   * pessoa, e este cálculo servia apenas para mostrar o campo de senha. Com M-023, as três
   * escritas de `/api/v1/my-account/users` (criar, editar, remover) exigem o mesmo papel e
   * respondem `403` para os demais — então o mesmo cálculo passa a esconder as ações que
   * não iriam funcionar, em vez de deixar o cliente descobrir pelo erro.
   */
  const isCurrentUserAdmin = useMemo(() => {
    if (!user || users.length === 0) return false;
    const currentAccountUser = users.find((u) => u.email === user.email);
    return currentAccountUser?.access_level === "admin";
  }, [user, users]);

  // Estados para Faturas
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [invoicesLoading, setInvoicesLoading] = useState(true);
  const [trialInfo, setTrialInfo] = useState<TrialInfo | null>(null);
  const [showInvoiceDialog, setShowInvoiceDialog] = useState(false);
  const [invoiceDetail, setInvoiceDetail] = useState<InvoiceDetail | null>(
    null,
  );
  const [invoiceDetailLoading, setInvoiceDetailLoading] = useState(false);

  // Estados para Planos
  const [eligiblePlans, setEligiblePlans] = useState<EligiblePlanItem[]>([]);
  const [canUpgrade, setCanUpgrade] = useState(false);
  const [upgradeBlockedReason, setUpgradeBlockedReason] = useState<
    string | null
  >(null);
  const [currentPlan, setCurrentPlan] = useState<CurrentPlan | null>(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const [selectedEligiblePlan, setSelectedEligiblePlan] =
    useState<EligiblePlanItem | null>(null);
  /**
   * A vitrine de quem está **sem plano** — o lojista que se cadastrou e saiu antes do
   * checkout. O suspenso não chega aqui: a suspensão não desliga `is_active`, e
   * `current_plan` continua trazendo o plano dele, com a fatura a pagar em Faturas. A aba só
   * sabia oferecer upgrade, e o
   * upgrade exige plano atual: para essa pessoa ela mostrava uma frase cinza e mais nada, e
   * assinar só era possível pelo redirecionamento logo depois do cadastro. `null` é "ainda
   * não carregou"; a lista só é buscada quando não há plano atual.
   */
  const [planosParaAssinar, setPlanosParaAssinar] = useState<Plan[] | null>(
    null,
  );
  // A carga da assinatura falhou: `currentPlan` nulo aqui é "não sei", e não "não tem".
  const [assinaturaFalhou, setAssinaturaFalhou] = useState(false);
  const navigate = useNavigate();

  const [showPassword, setShowPassword] = useState(false);
  const [showEditPasswordVisible, setShowEditPasswordVisible] = useState(false);

  // Estados para Data Deletion
  const [showDataDeletionDialog, setShowDataDeletionDialog] = useState(false);
  const [deletingData, setDeletingData] = useState(false);
  const [deletionRequestId, setDeletionRequestId] = useState<string | null>(
    null,
  );

  // Reset tab when subaccount state changes (billing/subscription hidden)
  useEffect(() => {
    if (semCobranca) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setActiveTab("users");
    }
  }, [semCobranca]);

  // -------------- Loaders de API ----------------

  /**
   * A listagem da equipe — pedida só a quem o servidor não vai recusar.
   *
   * <M-247> Antes ela rodava para **todo** mundo, e quem não administra a conta abria a
   * página com um cabeçalho de tabela sem linha nenhuma e um toast vermelho no canto, para
   * descrever um acesso perfeitamente normal: desde M-023 a rota exige `admin` (ou ser o
   * assinante dono) e responde `403 Apenas administradores podem gerenciar usuários da
   * conta.`.
   *
   * Quem responde a pergunta é `can_manage_account_users`, do `GET /users/me` — o próprio
   * guard, publicado. Quando ele vem `undefined` (resposta de um servidor anterior ao
   * campo), o plano B é **chamar e ler o `403`**, declarando-o esperado pelo cabeçalho, em
   * vez de deduzir o guard aqui — ver `administraAConta`.
   */
  const loadUsers = useCallback(async () => {
    if (administraOEscopo === false) {
      setUsers([]);
      setUsersLoading(false);
      return;
    }

    setUsersLoading(true);
    try {
      const { data: resp } = await listAccountUsersApiV1MyAccountUsersGet({
        headers: { [CABECALHO_403_ESPERADO]: "1" },
        throwOnError: true,
      });
      setUsers(resp.data.map(mapAccountUser));
      setRecusadaPeloServidor(false);
    } catch (err: unknown) {
      // O `403` é a resposta do plano B, e não uma falha: quem chegou aqui sem o campo no
      // perfil acabou de descobrir que não administra a conta.
      if (err instanceof Error && (err as HeyApiError).status === 403) {
        setUsers([]);
        setRecusadaPeloServidor(true);
      } else {
        console.error("Erro ao carregar usuários:", err);
      }
    } finally {
      setUsersLoading(false);
    }
  }, [administraOEscopo]);

  /**
   * Carrega os times e as lojas que o formulário oferece.
   *
   * As duas chamadas falham em silêncio de propósito, e cada `403` significa uma coisa
   * legítima: quem não administra a agência não tem times a oferecer, e quem não é assinante
   * não lista contas. A tela mostra a mensagem de lista vazia do próprio seletor, que é
   * mais honesta do que um erro — não há nada quebrado, só não há o que escolher.
   */
  const loadEscopoDoFormulario = useCallback(async () => {
    setEscopoCarregando(true);

    const semFiltroGlobal = { "x-ignore-global-filters": "1" };

    const [times, contas] = await Promise.all([
      apiV1TeamsGet({ headers: semFiltroGlobal, throwOnError: true })
        .then((resposta) => resposta.data.data)
        .catch(() => [] as TeamItem[]),
      listAccountsApiV1AccountsGet({
        headers: semFiltroGlobal,
        throwOnError: true,
      })
        .then((resposta) => {
          const contas = resposta.data.accounts ?? [];

          // A **conta principal** fica de fora: o servidor recusa credencial de cliente
          // nela (D-060), porque um vínculo cedido ali daria leitura sobre o
          // `customer_uuid` de cada funcionário da agência. Ela é a conta mais antiga do
          // assinante — o mesmo critério de `TeamRepository.matrizDaAgencia`, com o `id`
          // como desempate —, e só é excluída quando há outra: na agência de conta única
          // ela é a loja, e tirá-la deixaria a lista vazia.
          const matriz =
            contas.length > 1
              ? [...contas].sort(
                  (a, b) =>
                    a.created_at.localeCompare(b.created_at) ||
                    a.id.localeCompare(b.id),
                )[0]
              : undefined;

          return contas
            .filter((loja) => loja.id !== matriz?.id)
            .map((loja) => ({
              id: loja.id,
              name: loja.name,
              hint: loja.slug,
            }));
        })
        .catch(() =>
          // O dono de uma loja não lista contas do assinante; as contas do perfil dele são
          // as que ele administra, e são as únicas que o servidor aceitaria mesmo.
          (user?.accounts ?? []).map((conta) => ({
            id: conta.id,
            name: conta.name,
            hint: conta.slug,
          })),
        ),
    ]);

    setTimesDaAgencia(times);
    setLojasDaAgencia(contas);
    setEscopoCarregando(false);
  }, [user]);

  const loadInvoices = useCallback(async () => {
    setInvoicesLoading(true);
    try {
      const { data: resp } = await listInvoicesApiV1MyAccountInvoicesGet({
        throwOnError: true,
      });
      setInvoices(resp.data.map(mapInvoice));
      setTrialInfo(resp.trial_info ? mapTrialInfo(resp.trial_info) : null);
    } catch (err: unknown) {
      console.error("Erro ao carregar faturas:", err);
    } finally {
      setInvoicesLoading(false);
    }
  }, []);

  /**
   * O saldo da loja ativa. A resposta que chega depois de a loja mudar é descartada, e a troca
   * de loja apaga o saldo da anterior antes de pedir o da nova: senão o card mostraria o saldo
   * de uma loja sob o nome da outra.
   */
  const lojaDosCreditosRef = useRef(lojaDosCreditos);
  // Declarado antes do efeito que carrega o saldo, e por isso roda antes dele.
  useEffect(() => {
    lojaDosCreditosRef.current = lojaDosCreditos;
  }, [lojaDosCreditos]);
  const loadAiCredits = useCallback(async () => {
    const loja = lojaDosCreditos;
    if (loja === null) {
      setAiCredits(null);
      return;
    }
    try {
      const saldo = await fetchSaldoDeIa(loja);
      if (lojaDosCreditosRef.current === loja) setAiCredits(saldo);
    } catch (creditsErr: unknown) {
      if (lojaDosCreditosRef.current === loja) setAiCredits(null);
      console.error("Erro ao carregar créditos de IA:", creditsErr);
    }
  }, [lojaDosCreditos]);

  const loadSubscription = useCallback(async () => {
    setSubscriptionLoading(true);
    try {
      const { data: resp } =
        await getSubscriptionInfoApiV1MyAccountSubscriptionGet({
          throwOnError: true,
        });
      setCurrentPlan(
        resp.current_plan ? mapCurrentPlan(resp.current_plan) : null,
      );
      setAssinaturaFalhou(false);
      // Fallback de gating caso o fetch de planos elegíveis falhe.
      setCanUpgrade(resp.can_upgrade ?? false);
      setUpgradeBlockedReason(resp.upgrade_blocked_reason ?? null);
    } catch (err: unknown) {
      console.error("Erro ao carregar assinatura:", err);
      setAssinaturaFalhou(true);
      toast({
        title: "Erro",
        description: "Não foi possível carregar dados da assinatura.",
        variant: "destructive",
      });
    } finally {
      setSubscriptionLoading(false);
    }
  }, []);

  const loadEligiblePlans = useCallback(async () => {
    try {
      const resp = await fetchEligiblePlans();
      // Ordena do menor para o maior preço mensal.
      const plans = [...(resp.eligible_plans ?? [])].sort(
        (a, b) => Number(a.plan.price_monthly) - Number(b.plan.price_monthly),
      );
      setEligiblePlans(plans);
      setCanUpgrade(resp.can_upgrade ?? false);
      setUpgradeBlockedReason(resp.upgrade_blocked_reason ?? null);
    } catch (err: unknown) {
      console.error("Erro ao carregar planos elegíveis:", err);
    }
  }, []);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    loadUsers();
    if (!semCobranca) {
      loadInvoices();
      loadSubscription();
      loadEligiblePlans();
    }
  }, [
    loadUsers,
    loadInvoices,
    loadSubscription,
    loadEligiblePlans,
    semCobranca,
  ]);

  const semPlanoAtual =
    !semCobranca && !subscriptionLoading && !assinaturaFalhou && !currentPlan;
  useEffect(() => {
    if (!semPlanoAtual || planosParaAssinar !== null) return;
    planService
      .listPublicPlans()
      .then((plans) => setPlanosParaAssinar(planosDoCadastro(plans)))
      .catch((err: unknown) => {
        console.error("Erro ao carregar planos para assinar:", err);
        setPlanosParaAssinar([]);
      });
  }, [semPlanoAtual, planosParaAssinar]);

  const assinarPlano = (plan: Plan) => {
    navigate("/register/checkout", {
      state: {
        name: user?.name,
        email: user?.email,
        plan,
        billingCycle: "monthly",
        planCategory: CATEGORIA_DO_CADASTRO,
      },
    });
  };

  // <M-280> O saldo é da loja ativa, então ele tem o próprio gatilho: muda quando a loja muda,
  // sem arrastar junto a assinatura, as faturas e os planos, que são do assinante.
  useEffect(() => {
    if (semCobranca) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setAiCredits(null);
    loadAiCredits();
  }, [loadAiCredits, semCobranca]);

  // Recarrega os dados da página ao trocar de conta pelo seletor
  // (sem redirecionar para /dashboard nesta página).
  // Só para quem pode trocar de conta — com uma conta só, nada muda.
  const lastAccountId = useRef(selectedAccount?.id ?? null);
  useEffect(() => {
    if (!canSwitchAccounts) return;
    const accountId = selectedAccount?.id ?? null;
    if (lastAccountId.current === accountId) return;
    lastAccountId.current = accountId;
    loadUsers();
    if (!semCobranca) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      loadInvoices();
      loadSubscription();
      loadEligiblePlans();
    }
  }, [
    selectedAccount,
    semCobranca,
    canSwitchAccounts,
    loadUsers,
    loadInvoices,
    loadSubscription,
    loadEligiblePlans,
  ]);

  // -------------- Handlers de Usuários ----------------

  const handleAddUser = async () => {
    if (
      !userFormData.name.trim() ||
      !userFormData.email.trim() ||
      !userFormData.password.trim()
    ) {
      toast({
        title: "Campos obrigatórios",
        description: "Preencha todos os campos obrigatórios.",
        variant: "destructive",
      });
      return;
    }

    // A credencial de cliente sem loja nenhuma não enxerga nada, e a API a recusa com `422`.
    // Dizer isso aqui é mais útil do que traduzir o erro de validação depois.
    if (
      userFormData.accessType === "client" &&
      userFormData.accountIds.length === 0
    ) {
      toast({
        title: "Escolha as lojas",
        description:
          "Uma credencial de cliente precisa de ao menos uma loja para acompanhar.",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const body: AccountUserCreate = {
        name: userFormData.name,
        email: userFormData.email,
        password: userFormData.password,
        access_level: userFormData.accessLevel,
        access_type: userFormData.accessType,
        // Cada tipo manda **só** a sua lista: o contrato recusa o corpo que traz as duas.
        ...(userFormData.accessType === "client"
          ? { account_ids: userFormData.accountIds }
          : { team_ids: userFormData.teamIds }),
      };
      await createAccountUserApiV1MyAccountUsersPost({
        body,
        throwOnError: true,
      });
      setShowAddUserDialog(false);
      resetUserForm();
      toast({
        title: "Usuário adicionado",
        description: "O novo usuário já pode acessar a plataforma.",
      });
      await loadUsers();
    } catch (error: unknown) {
      const msg = getErrorMessage(error);
      toast({
        title: "Erro ao adicionar usuário",
        description: msg,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEditUser = async () => {
    if (!selectedUser) return;

    if (
      selectedUser.access_type === "client" &&
      userFormData.accountIds.length === 0
    ) {
      toast({
        title: "Escolha as lojas",
        description:
          "Uma credencial de cliente precisa de ao menos uma loja. Para tirá-la de circulação, remova o usuário.",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const updatePayload: AccountUserUpdate = {
        name: userFormData.name || undefined,
        email: userFormData.email || undefined,
        access_level: userFormData.accessLevel || undefined,
        // O tipo da credencial não se troca por aqui — só o conjunto que cabe nela. Mandar
        // a lista **substitui** o conjunto inteiro, inclusive para esvaziá-lo: um
        // funcionário pode sair de todos os times, e é por isso que a lista vazia vai.
        ...(selectedUser.access_type === "client"
          ? { account_ids: userFormData.accountIds }
          : { team_ids: userFormData.teamIds }),
      };
      if (userFormData.password && userFormData.password.trim()) {
        updatePayload.password = userFormData.password;
      }
      await updateAccountUserApiV1MyAccountUsersAccountUserIdPut({
        path: { account_user_id: selectedUser.id },
        body: updatePayload,
        throwOnError: true,
      });
      setShowEditUserDialog(false);
      setSelectedUser(null);
      toast({
        title: "Usuário atualizado",
        description: "As alterações foram salvas com sucesso.",
      });
      await loadUsers();
    } catch (error: unknown) {
      const msg = getErrorMessage(error);
      toast({
        title: "Erro ao atualizar usuário",
        description: msg,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;

    setLoading(true);
    try {
      await deleteAccountUserApiV1MyAccountUsersAccountUserIdDelete({
        path: { account_user_id: selectedUser.id },
        throwOnError: true,
      });
      setShowDeleteUserDialog(false);
      setSelectedUser(null);
      toast({
        title: "Usuário removido",
        description: "O usuário foi removido com sucesso.",
      });
      await loadUsers();
    } catch (error: unknown) {
      const msg = getErrorMessage(error);
      toast({
        title: "Erro ao remover usuário",
        description: msg,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDataDeletion = async () => {
    setDeletingData(true);
    try {
      const { data: response } = await requestDataDeletionApiV1DataDeletionPost(
        {
          throwOnError: true,
        },
      );
      setDeletionRequestId(response.deletion_request_id);
      toast({
        title: "Dados excluídos com sucesso",
        description: `ID da solicitação: ${response.deletion_request_id}`,
      });
    } catch {
      toast({
        title: "Erro ao excluir dados",
        description: "Tente novamente mais tarde.",
        variant: "destructive",
      });
    } finally {
      setDeletingData(false);
    }
  };

  /** O formulário em branco — o mesmo estado inicial, num lugar só. */
  const resetUserForm = () =>
    setUserFormData({
      name: "",
      email: "",
      password: "",
      accessLevel: "viewer",
      accessType: "team",
      teamIds: [],
      accountIds: [],
    });

  const openAddUserDialog = () => {
    resetUserForm();
    // Carrega ao **abrir**, e não no carregamento da página: as duas rotas são de gestão e
    // respondem `403` para quem não administra, que é quem nunca abre este formulário.
    // Recarregar a cada abertura também mantém a lista viva — um time criado na outra tela
    // aparece aqui sem recarregar a página.
    void loadEscopoDoFormulario();
    setShowAddUserDialog(true);
  };

  const openEditUserDialog = (u: AccountUser) => {
    setSelectedUser(u);
    setUserFormData({
      name: u.name,
      email: u.email,
      password: "",
      accessLevel: u.access_level,
      accessType: u.access_type,
      teamIds: u.teams.map((time) => time.id),
      accountIds: u.accounts.map((loja) => loja.account_id),
    });
    setShowEditPassword(false);
    void loadEscopoDoFormulario();
    setShowEditUserDialog(true);
  };

  const openDeleteUserDialog = (u: AccountUser) => {
    setSelectedUser(u);
    setShowDeleteUserDialog(true);
  };

  // -------------- Handler de Fatura ----------------

  const handleOpenInvoice = async (invoice: Invoice) => {
    setShowInvoiceDialog(true);
    setInvoiceDetailLoading(true);
    setInvoiceDetail(null);
    try {
      const { data: detail } =
        await getInvoiceDetailApiV1MyAccountInvoicesInvoiceIdGet({
          path: { invoice_id: invoice.id },
          throwOnError: true,
        });
      setInvoiceDetail(mapInvoiceDetail(detail));
    } catch (err: unknown) {
      console.error("Erro ao carregar detalhe da fatura:", err);
      toast({
        title: "Erro",
        description: "Não foi possível carregar os detalhes da fatura.",
        variant: "destructive",
      });
      setShowInvoiceDialog(false);
    } finally {
      setInvoiceDetailLoading(false);
    }
  };

  // -------------- Formatadores ----------------

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);

  const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString("pt-BR");
  };

  // -------------- Opções dos seletores do formulário ----------------

  const opcoesDeTime: TeamMultiSelectOption[] = timesDaAgencia.map((time) => ({
    id: time.id,
    label: time.name,
    hint: time.description,
    badge: `${time.member_count} membro(s)`,
    muted: !time.is_active,
  }));

  const opcoesDeLoja: TeamMultiSelectOption[] = lojasDaAgencia.map((loja) => ({
    id: loja.id,
    label: loja.name,
    hint: loja.hint ?? null,
  }));

  // -------------- Colunas das tabelas ----------------

  const usersColumns: TableColumn[] = [
    {
      key: "name",
      header: "Nome",
      render: (value: string | null | undefined, row: TableRow) => (
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
            <UserIcon className="h-4 w-4 text-primary" />
          </div>
          <div>
            <p className="font-medium">{value}</p>
            <p className="text-xs text-muted-foreground">
              {typeof row.email === "string" ? row.email : ""}
            </p>
          </div>
        </div>
      ),
    },
    {
      key: "access_level",
      header: "Pode",
      render: (value: AccountUser["access_level"]) => {
        const cores: Record<AccessLevel, string> = {
          admin: "bg-purple-100 text-purple-800",
          editor: "bg-blue-100 text-blue-800",
          viewer: "bg-gray-100 text-gray-800",
        };
        return (
          <Badge className={cores[value] ?? "bg-gray-100 text-gray-800"}>
            {ROTULO_DO_NIVEL[value] ?? value}
          </Badge>
        );
      },
    },
    {
      key: "access_type",
      header: "Acesso",
      render: (value: AccountUser["access_type"], row: TableRow) => {
        if (!isAccountUser(row)) return null;
        const ehCliente = value === "client";
        const escopo = ehCliente
          ? row.accounts.map((loja) => loja.name)
          : row.teams.map((time) => time.name);
        return (
          <div className="space-y-1">
            <Badge
              className={
                ehCliente
                  ? "bg-emerald-100 text-emerald-800"
                  : "bg-indigo-100 text-indigo-800"
              }
            >
              {ehCliente ? "Cliente" : "Equipe"}
            </Badge>
            <p className="text-xs text-muted-foreground">
              {escopo.length > 0
                ? escopo.join(", ")
                : ehCliente
                  ? "Sem loja"
                  : "Sem time"}
            </p>
          </div>
        );
      },
    },
    {
      key: "status",
      header: "Status",
      render: (value: AccountUser["status"]) => {
        const cfg: Record<
          string,
          { label: string; icon: LucideIcon; color: string }
        > = {
          active: {
            label: "Ativo",
            icon: CheckCircle,
            color: "text-green-600",
          },
          pending: {
            label: "Pendente",
            icon: Clock,
            color: "text-yellow-600",
          },
          inactive: {
            label: "Inativo",
            icon: XCircle,
            color: "text-red-600",
          },
        };
        const c = cfg[value] || cfg.pending;
        const Icon = c.icon;
        return (
          <div className={`flex items-center gap-1 ${c.color}`}>
            <Icon className="h-4 w-4" />
            <span className="text-sm">{c.label}</span>
          </div>
        );
      },
    },
    {
      key: "last_login",
      header: "Último Acesso",
      render: (value: string | null | undefined) =>
        value ? formatDate(value) : "Nunca acessou",
    },
    {
      key: "actions",
      header: "Ações",
      render: (_value: unknown, row: TableRow) => {
        // M-023: editar e remover respondem `403` para quem não é `admin`/`owner`.
        if (!isAccountUser(row) || !isCurrentUserAdmin) return null;
        return (
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => openEditUserDialog(row)}
            >
              <Edit className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="text-red-600 hover:text-red-700"
              onClick={() => openDeleteUserDialog(row)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        );
      },
    },
  ];

  const invoicesColumns: TableColumn[] = [
    {
      key: "invoice_number",
      header: "Fatura",
      render: (value: string | null | undefined, row: TableRow) => (
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
            <FileText className="h-4 w-4 text-primary" />
          </div>
          <div>
            <p className="font-medium text-sm">{value || "—"}</p>
            <p className="text-xs text-muted-foreground">
              {typeof row.description === "string"
                ? row.description
                : `${String(row.month ?? "")}/${String(row.year ?? "")}`}
            </p>
          </div>
        </div>
      ),
    },
    {
      key: "amount",
      header: "Valor",
      render: (value: number) => (
        <span className="font-semibold">{formatCurrency(value)}</span>
      ),
    },
    {
      key: "due_date",
      header: "Vencimento",
      render: (value: string) => (
        <div className="flex items-center gap-1.5 text-sm">
          <CalendarDays className="h-3.5 w-3.5 text-muted-foreground" />
          {formatDate(value)}
        </div>
      ),
    },
    {
      key: "status",
      header: "Status",
      render: (value: Invoice["status"], row: TableRow) => {
        const statusConfig: Record<
          string,
          { label: string; icon: LucideIcon; color: string }
        > = {
          paid: {
            label: "Paga",
            icon: CheckCircle,
            color: "bg-green-100 text-green-800",
          },
          pending: {
            label: "Pendente",
            icon: Clock,
            color: "bg-yellow-100 text-yellow-800",
          },
          overdue: {
            label: "Atrasada",
            icon: AlertTriangle,
            color: "bg-red-100 text-red-800",
          },
          cancelled: {
            label: "Cancelada",
            icon: XCircle,
            color: "bg-gray-100 text-gray-800",
          },
        };
        const config = statusConfig[value] || statusConfig.pending;
        const Icon = config.icon;
        return (
          <Badge className={config.color}>
            <Icon className="h-3 w-3 mr-1" />
            {config.label}
            {value === "paid" &&
              typeof row.paid_at === "string" &&
              ` em ${formatDate(row.paid_at)}`}
          </Badge>
        );
      },
    },
    {
      key: "actions",
      header: "",
      render: (_value: unknown, row: TableRow) => {
        if (!isInvoice(row)) return null;
        return (
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5 text-xs"
            onClick={() => handleOpenInvoice(row)}
          >
            <FileText className="h-3.5 w-3.5" />
            Ver Fatura
          </Button>
        );
      },
    },
  ];

  const openUpgradeDialog = (plan: EligiblePlanItem | null) => {
    setSelectedEligiblePlan(plan);
    setShowUpgradeDialog(true);
  };

  const blockedMessage = getUpgradeBlockedMessage(upgradeBlockedReason);

  /**
   * <M-216> Dentro de uma loja, esta página mistura dois donos: Usuários é da loja
   * selecionada, e Faturas e Assinatura são de quem está logado. O aviso existe para que a
   * mistura seja dita em vez de deduzida.
   *
   * ⚠️ **A frase não pode nomear um lugar para onde ir.** A primeira versão dizia "e não de
   * *Loja Beta*", e isso é uma pista falsa justamente para quem este item existe para
   * servir: o lojista multi-conta não tem "Conta Principal" no seletor dele (M-214), então
   * a frase apontaria para um lugar que não existe — e ele paga **uma** assinatura, que
   * cobre todas as lojas. A frase que sobrou é verdadeira para as duas pessoas que a leem,
   * a agência dentro da loja de um cliente e o lojista dentro da própria loja, e não manda
   * ninguém a lugar nenhum.
   */
  const avisoDeCobrancaDoTitular =
    selectedAccount && !semCobranca ? (
      <p className="text-sm text-muted-foreground">
        Faturas e assinatura são da sua assinatura, que cobre todas as suas
        lojas — e não da loja selecionada.
      </p>
    ) : null;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <header className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            {isAgencyUser ? (
              <Building2 className="h-8 w-8" />
            ) : (
              <Store className="h-8 w-8" />
            )}
            Minha Conta
          </h1>
          <p className="text-muted-foreground">
            {semCobranca
              ? "Gerencie quem tem acesso ao painel"
              : "Gerencie usuários, faturas e sua assinatura"}
          </p>
        </div>
        {canSwitchAccounts && (
          <div className="w-full sm:w-72">
            <AccountSelector onOpen={() => setShowAccountModal(true)} />
          </div>
        )}
      </header>

      <AccountSelectionModal
        open={showAccountModal}
        onOpenChange={setShowAccountModal}
        redirectOnSwitch={false}
      />

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onValueChange={setActiveTab}
        className="space-y-6"
      >
        <TabsList
          className={`grid w-full ${semCobranca ? "grid-cols-1" : "grid-cols-3"} lg:w-[500px]`}
        >
          <TabsTrigger value="users" className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            Usuários
          </TabsTrigger>
          {!semCobranca && (
            <TabsTrigger value="billing" className="flex items-center gap-2">
              <Receipt className="h-4 w-4" />
              Faturas
            </TabsTrigger>
          )}
          {!semCobranca && (
            <TabsTrigger
              value="subscription"
              className="flex items-center gap-2"
            >
              <Crown className="h-4 w-4" />
              Assinatura
            </TabsTrigger>
          )}
        </TabsList>

        {/* Tab: Usuários */}
        <TabsContent value="users" className="space-y-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Usuários da Conta
                </CardTitle>
                <CardDescription>
                  Gerencie quem tem acesso ao painel e suas permissões
                </CardDescription>
              </div>
              {/* M-023: criar usuário exige papel `admin`/`owner` na conta. */}
              {podeGerirUsuarios && isCurrentUserAdmin && (
                <Button onClick={openAddUserDialog}>
                  <Plus className="h-4 w-4 mr-2" />
                  Novo Usuário
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {usersLoading ? (
                <Skeleton className="h-48 w-full" />
              ) : podeGerirUsuarios ? (
                <StandardizedTable
                  title="Usuários da conta"
                  columns={usersColumns}
                  data={users}
                  mensagemVazia="Nenhum usuário nesta conta."
                  showHeader={false}
                  variant="plain"
                  hideTotals
                />
              ) : (
                <MeuAcesso acessos={meusAcessos} />
              )}
            </CardContent>
          </Card>

          {/* Permissões */}
          {podeGerirUsuarios && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="h-5 w-5" />
                  Níveis de Permissão
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4 md:grid-cols-3">
                  <div className="p-4 border rounded-lg">
                    <Badge className="bg-purple-100 text-purple-800 mb-2">
                      Administrador
                    </Badge>
                    <p className="text-sm text-muted-foreground">
                      Acesso total: gerenciar usuários, ver relatórios,
                      configurações e integrações.
                    </p>
                  </div>
                  <div className="p-4 border rounded-lg">
                    <Badge className="bg-blue-100 text-blue-800 mb-2">
                      Editor
                    </Badge>
                    <p className="text-sm text-muted-foreground">
                      Pode ver e editar relatórios, mas não gerencia usuários ou
                      configurações.
                    </p>
                  </div>
                  <div className="p-4 border rounded-lg">
                    <Badge className="bg-gray-100 text-gray-800 mb-2">
                      Visualizador
                    </Badge>
                    <p className="text-sm text-muted-foreground">
                      Acesso somente leitura aos relatórios e dashboards.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Tab: Faturas */}
        {!semCobranca && (
          <TabsContent value="billing" className="space-y-6">
            {avisoDeCobrancaDoTitular}
            {invoicesLoading ? (
              <div className="space-y-6">
                <Skeleton className="h-48 w-full" />
                <Skeleton className="h-64 w-full" />
              </div>
            ) : (
              <>
                {/* Card de Resumo: Trial / Próxima Fatura */}
                {trialInfo && (
                  <div className="grid gap-4 md:grid-cols-2">
                    {/* Card do Período Grátis */}
                    {trialInfo.is_in_trial && (
                      <Card className="border-emerald-200 bg-gradient-to-br from-emerald-50 to-green-50">
                        <CardContent className="pt-6">
                          <div className="flex items-start gap-4">
                            <div className="h-12 w-12 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
                              <Gift className="h-6 w-6 text-emerald-600" />
                            </div>
                            <div className="flex-1">
                              <h3 className="font-semibold text-emerald-900 text-lg">
                                Período Grátis Ativo
                              </h3>
                              <p className="text-sm text-emerald-700 mt-1">
                                Você está no período de teste gratuito do plano{" "}
                                <strong>{trialInfo.plan_name}</strong>.
                              </p>
                              <div className="mt-3 flex items-center gap-2">
                                <div className="flex items-center gap-1.5 bg-emerald-100 text-emerald-800 rounded-full px-3 py-1 text-sm font-medium">
                                  <Clock className="h-3.5 w-3.5" />
                                  {trialInfo.trial_days_remaining}{" "}
                                  {trialInfo.trial_days_remaining === 1
                                    ? "dia restante"
                                    : "dias restantes"}
                                </div>
                              </div>
                              {trialInfo.trial_end && (
                                <p className="text-xs text-emerald-600 mt-2">
                                  Expira em {formatDate(trialInfo.trial_end)}
                                </p>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {/* Card da Próxima Fatura */}
                    {(() => {
                      const nextInvoice = invoices.find(
                        (inv) =>
                          inv.status === "pending" || inv.status === "overdue",
                      );
                      if (!nextInvoice) return null;
                      const isOverdue = nextInvoice.status === "overdue";
                      return (
                        <Card
                          className={
                            isOverdue
                              ? "border-red-200 bg-gradient-to-br from-red-50 to-orange-50"
                              : "border-blue-200 bg-gradient-to-br from-blue-50 to-indigo-50"
                          }
                        >
                          <CardContent className="pt-6">
                            <div className="flex items-start gap-4">
                              <div
                                className={`h-12 w-12 rounded-xl flex items-center justify-center flex-shrink-0 ${isOverdue ? "bg-red-100" : "bg-blue-100"}`}
                              >
                                <CreditCard
                                  className={`h-6 w-6 ${isOverdue ? "text-red-600" : "text-blue-600"}`}
                                />
                              </div>
                              <div className="flex-1">
                                <h3
                                  className={`font-semibold text-lg ${isOverdue ? "text-red-900" : "text-blue-900"}`}
                                >
                                  {isOverdue
                                    ? "Fatura Vencida"
                                    : "Próxima Fatura"}
                                </h3>
                                <p
                                  className={`text-sm mt-1 ${isOverdue ? "text-red-700" : "text-blue-700"}`}
                                >
                                  {nextInvoice.description ||
                                    `${nextInvoice.month}/${nextInvoice.year}`}
                                </p>
                                <div className="mt-3 flex items-center justify-between">
                                  <div>
                                    <p
                                      className={`text-2xl font-bold ${isOverdue ? "text-red-900" : "text-blue-900"}`}
                                    >
                                      {formatCurrency(nextInvoice.amount)}
                                    </p>
                                    <div
                                      className={`flex items-center gap-1.5 text-xs mt-1 ${isOverdue ? "text-red-600" : "text-blue-600"}`}
                                    >
                                      <CalendarDays className="h-3 w-3" />
                                      Vencimento:{" "}
                                      {formatDate(nextInvoice.due_date)}
                                    </div>
                                  </div>
                                  <Button
                                    size="sm"
                                    variant={
                                      isOverdue ? "destructive" : "default"
                                    }
                                    className="gap-1.5"
                                    onClick={() =>
                                      handleOpenInvoice(nextInvoice)
                                    }
                                  >
                                    <FileText className="h-3.5 w-3.5" />
                                    Ver Fatura
                                  </Button>
                                </div>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })()}
                  </div>
                )}

                {/* Tabela de Histórico de Faturas */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Receipt className="h-5 w-5" />
                      Histórico de Faturas
                    </CardTitle>
                    <CardDescription>
                      Todas as faturas da sua assinatura no Painel do E-Commerce
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {invoices.length === 0 ? (
                      <div className="text-center py-12">
                        <Receipt className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                        <p className="text-muted-foreground font-medium">
                          Nenhuma fatura encontrada
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">
                          Suas faturas aparecerão aqui assim que forem geradas.
                        </p>
                      </div>
                    ) : (
                      <StandardizedTable
                        title="Faturas"
                        columns={invoicesColumns}
                        data={invoices}
                        showHeader={false}
                        variant="plain"
                        hideTotals
                      />
                    )}
                  </CardContent>
                </Card>
              </>
            )}
          </TabsContent>
        )}

        {/* Tab: Assinatura */}
        {!semCobranca && (
          <TabsContent value="subscription" className="space-y-6">
            {avisoDeCobrancaDoTitular}
            {subscriptionLoading ? (
              <Skeleton className="h-64 w-full" />
            ) : (
              <>
                {/* Créditos de IA */}
                {aiCredits && lojaDosCreditos && (
                  <Card>
                    <CardHeader>
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <CardTitle>Créditos de IA</CardTitle>
                          <CardDescription>
                            {nomeDaLojaDosCreditos
                              ? `Saldo da loja ${nomeDaLojaDosCreditos} para as ferramentas de IA.`
                              : "Saldo desta loja para as ferramentas de IA."}
                          </CardDescription>
                        </div>
                        {!aiCredits.unlimited && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setBuyCreditsOpen(true)}
                          >
                            <Coins className="h-4 w-4 mr-2" />
                            Comprar créditos
                          </Button>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent>
                      {aiCredits.unlimited ? (
                        <span className="text-2xl font-semibold text-primary">
                          Ilimitado
                        </span>
                      ) : (
                        <div className="space-y-1">
                          <div className="flex items-baseline gap-2">
                            <span className="text-2xl font-semibold text-primary">
                              {aiCredits.total ?? 0}
                            </span>
                            <span className="text-sm text-muted-foreground">
                              créditos disponíveis
                            </span>
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {aiCredits.franquia ?? 0}
                            {aiCredits.franquiaDoCiclo != null &&
                              ` de ${aiCredits.franquiaDoCiclo}`}{" "}
                            da franquia do mês
                            {aiCredits.comprado > 0 &&
                              ` · ${aiCredits.comprado} comprados, sem validade`}
                          </p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* Plano Atual */}
                {currentPlan && (
                  <Card className="border-primary">
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <div>
                          <CardTitle className="flex items-center gap-2">
                            <Crown className="h-5 w-5 text-yellow-500" />
                            Plano Atual
                          </CardTitle>
                          <CardDescription>
                            Sua assinatura ativa no Painel do E-Commerce
                          </CardDescription>
                        </div>
                        <Badge className="bg-primary text-primary-foreground text-lg px-4 py-1">
                          {currentPlan.name}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                        <div>
                          <p className="text-3xl font-bold">
                            {formatCurrency(currentPlan.price)}
                            <span className="text-sm font-normal text-muted-foreground">
                              /mês
                            </span>
                          </p>
                          {currentPlan.status && (
                            <Badge className="mt-1" variant="outline">
                              {currentPlan.status === "trial"
                                ? emPeriodoGratis(currentPlan.trial_end)
                                  ? "Período de teste"
                                  : "Aguardando pagamento"
                                : currentPlan.status === "active"
                                  ? "Ativa"
                                  : currentPlan.status}
                            </Badge>
                          )}
                          {currentPlan.features &&
                            currentPlan.features.length > 0 && (
                              <ul className="mt-2 space-y-1">
                                {currentPlan.features.map((feature, idx) => (
                                  <li
                                    key={idx}
                                    className="flex items-center gap-2 text-sm text-muted-foreground"
                                  >
                                    <CheckCircle className="h-4 w-4 text-green-500" />
                                    {feature}
                                  </li>
                                ))}
                              </ul>
                            )}
                        </div>
                        {canUpgrade && (
                          <Button
                            variant="outline"
                            onClick={() =>
                              openUpgradeDialog(eligiblePlans[0] ?? null)
                            }
                          >
                            Alterar Plano
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Sem plano atual: a vitrine para assinar. Ela substitui a frase de
                    bloqueio, que aqui só diria "Você não possui uma assinatura ativa." */}
                {semPlanoAtual && (
                  <Card className="border-primary">
                    <CardHeader>
                      <div className="flex flex-wrap items-start justify-between gap-4">
                        <div>
                          <CardTitle className="flex items-center gap-2">
                            <Crown className="h-5 w-5 text-yellow-500" />
                            Escolha seu plano
                          </CardTitle>
                          <CardDescription>
                            Você ainda não tem um plano ativo. Assine para
                            liberar os painéis e as integrações.
                          </CardDescription>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate("/register/plans")}
                        >
                          Comparar planos
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {planosParaAssinar === null ? (
                        <Skeleton className="h-40 w-full" />
                      ) : planosParaAssinar.length === 0 ? (
                        <p className="text-sm text-muted-foreground">
                          Não foi possível carregar os planos agora. Tente
                          novamente em instantes.
                        </p>
                      ) : (
                        <div className="grid gap-6 md:grid-cols-3">
                          {planosParaAssinar.map((plan) => (
                            <div
                              key={plan.id}
                              className="flex flex-col p-6 border rounded-xl hover:border-primary/50 hover:shadow-md transition-all"
                            >
                              <h3 className="text-lg font-semibold">
                                {plan.name}
                              </h3>
                              {plan.description && (
                                <p className="mt-1 text-sm text-muted-foreground">
                                  {plan.description}
                                </p>
                              )}
                              <p className="mt-4 text-3xl font-bold">
                                {formatCurrency(plan.price_monthly)}
                                <span className="text-sm font-normal text-muted-foreground">
                                  /mês
                                </span>
                              </p>
                              {plan.trial_days > 0 && (
                                <Badge className="mt-2 w-fit" variant="outline">
                                  {plan.trial_days} dias grátis
                                </Badge>
                              )}
                              <ul className="mt-4 flex-1 space-y-1">
                                {plan.plan_features
                                  .filter((feature) => feature.is_enabled)
                                  .map((feature) => (
                                    <li
                                      key={feature.id}
                                      className="flex items-center gap-2 text-sm text-muted-foreground"
                                    >
                                      <CheckCircle className="h-4 w-4 shrink-0 text-green-500" />
                                      {feature.name}
                                    </li>
                                  ))}
                              </ul>
                              <Button
                                className="mt-6 w-full"
                                onClick={() => assinarPlano(plan)}
                              >
                                Assinar
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}

                {!semPlanoAtual && !canUpgrade && blockedMessage && (
                  <p className="text-sm text-muted-foreground">
                    {blockedMessage}
                  </p>
                )}

                {/* Planos Elegíveis para Upgrade */}
                {eligiblePlans.length > 0 && canUpgrade && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Sparkles className="h-5 w-5" />
                        Planos Elegíveis para Upgrade
                      </CardTitle>
                      <CardDescription>
                        O valor é proporcional aos dias restantes do seu ciclo
                        de cobrança atual.
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="grid gap-6 md:grid-cols-3">
                        {eligiblePlans.map((item) => {
                          const plan = item.plan;
                          const isCurrent = currentPlan?.id === plan.id;
                          return (
                            <div
                              key={plan.id}
                              className={`relative p-6 border rounded-xl transition-all ${
                                isCurrent
                                  ? "border-primary bg-primary/5 ring-2 ring-primary"
                                  : "hover:border-primary/50 hover:shadow-md"
                              }`}
                            >
                              {isCurrent && (
                                <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary">
                                  Plano Atual
                                </Badge>
                              )}
                              <h3 className="text-xl font-bold text-center">
                                {plan.name}
                              </h3>
                              <p className="text-3xl font-bold text-center my-4">
                                {formatCurrency(Number(plan.price_monthly))}
                                <span className="text-sm font-normal text-muted-foreground">
                                  /mês
                                </span>
                              </p>
                              <p className="text-center text-xs text-muted-foreground mb-2">
                                {formatCurrency(
                                  Number(
                                    item.proration_preview.prorated_difference,
                                  ),
                                )}{" "}
                                agora ·{" "}
                                {formatCurrency(
                                  Number(
                                    item.proration_preview.next_renewal_amount,
                                  ),
                                )}
                                /mês a partir de{" "}
                                {formatDate(
                                  item.proration_preview.next_renewal_date,
                                )}
                              </p>
                              <Separator className="my-4" />
                              {plan.features &&
                                Object.keys(plan.features).length > 0 && (
                                  <ul className="space-y-2 mb-6">
                                    {featuresToList(plan.features).map(
                                      (feature, idx) => (
                                        <li
                                          key={idx}
                                          className="flex items-center gap-2 text-sm"
                                        >
                                          <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                                          {feature}
                                        </li>
                                      ),
                                    )}
                                  </ul>
                                )}
                              {!isCurrent && (
                                <Button
                                  className="w-full"
                                  onClick={() => openUpgradeDialog(item)}
                                >
                                  <ArrowUpCircle className="h-4 w-4 mr-2" />
                                  Fazer Upgrade
                                </Button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </>
            )}
          </TabsContent>
        )}
      </Tabs>

      {/*
        Zona de Perigo — Data Deletion.

        <M-247> Só para quem é assinante. `POST /api/v1/data-deletion` alcança as lojas do
        **assinante** do usuário (`AccountPurgeRepository.contasDoAssinanteDoUsuario`), e
        para quem não tem linha em `subscribers` essa lista é vazia: medido em 2026-09-06
        com a credencial de cliente, a rota responde `200 Dados de integrações removidos
        com sucesso.` **sem apagar nada**. O botão não era perigoso para ela — era mentira,
        que é a outra metade do mesmo defeito desta página: não oferecer o que o servidor
        não vai fazer.
      */}
      {!semCobranca && (
        <Card className="border-destructive/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" />
              Zona de Perigo
            </CardTitle>
            <CardDescription>
              Ações irreversíveis relacionadas aos seus dados
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between p-4 border border-destructive/30 rounded-lg bg-destructive/5">
              <div className="space-y-1">
                <p className="font-semibold">Excluir dados de integrações</p>
                <p className="text-sm text-muted-foreground">
                  Remove permanentemente todos os dados do Google e Meta
                  (analytics, anúncios, insights). Sua conta permanece ativa.
                </p>
              </div>
              <Button
                variant="destructive"
                size="sm"
                className="shrink-0 ml-4"
                onClick={() => {
                  setDeletionRequestId(null);
                  setShowDataDeletionDialog(true);
                }}
              >
                Solicitar Exclusão
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Dialog: Adicionar Usuário */}
      <Dialog open={showAddUserDialog} onOpenChange={setShowAddUserDialog}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Adicionar Novo Usuário</DialogTitle>
            <DialogDescription>
              Convide um novo usuário para acessar o painel
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="user-name">Nome *</Label>
              <Input
                id="user-name"
                value={userFormData.name}
                onChange={(e) =>
                  setUserFormData((prev) => ({ ...prev, name: e.target.value }))
                }
                placeholder="Nome completo"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="user-email">E-mail *</Label>
              <Input
                id="user-email"
                type="email"
                value={userFormData.email}
                onChange={(e) =>
                  setUserFormData((prev) => ({
                    ...prev,
                    email: e.target.value,
                  }))
                }
                placeholder="email@exemplo.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="user-password">Senha *</Label>
              <div className="relative">
                <Input
                  id="user-password"
                  type={showPassword ? "text" : "password"}
                  value={userFormData.password}
                  onChange={(e) =>
                    setUserFormData((prev) => ({
                      ...prev,
                      password: e.target.value,
                    }))
                  }
                  placeholder="Senha inicial"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="user-access-type">Quem é *</Label>
              <Select
                value={userFormData.accessType}
                onValueChange={(value: AccessType) =>
                  setUserFormData((prev) => ({
                    ...prev,
                    accessType: value,
                    // O nível volta ao default da natureza escolhida. Isso também tira o
                    // `admin` do caminho ao trocar para cliente, que é a única combinação
                    // que o servidor recusa.
                    accessLevel: nivelPadraoDe(value),
                  }))
                }
              >
                <SelectTrigger id="user-access-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="team">Equipe</SelectItem>
                  <SelectItem value="client">Cliente</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {userFormData.accessType === "client"
                  ? "Alguém da loja. Enxerga as lojas marcadas abaixo — e nenhuma outra."
                  : "Funcionário da agência. Enxerga as lojas dos times de que participa."}
              </p>
            </div>
            <SeletorDeNivel
              id="user-access-level"
              accessType={userFormData.accessType}
              value={userFormData.accessLevel}
              onChange={(nivel) =>
                setUserFormData((prev) => ({ ...prev, accessLevel: nivel }))
              }
            />
            {userFormData.accessType === "team" ? (
              <div className="space-y-2">
                <Label>Times</Label>
                <TeamMultiSelect
                  options={opcoesDeTime}
                  selectedIds={userFormData.teamIds}
                  onChange={(ids) =>
                    setUserFormData((prev) => ({ ...prev, teamIds: ids }))
                  }
                  loading={escopoCarregando}
                  searchPlaceholder="Buscar time…"
                  emptyMessage="Nenhum time criado ainda. Você pode criar o usuário agora e adicioná-lo depois."
                />
              </div>
            ) : (
              <div className="space-y-2">
                <Label>Lojas *</Label>
                <TeamMultiSelect
                  options={opcoesDeLoja}
                  selectedIds={userFormData.accountIds}
                  onChange={(ids) =>
                    setUserFormData((prev) => ({ ...prev, accountIds: ids }))
                  }
                  loading={escopoCarregando}
                  searchPlaceholder="Buscar loja…"
                  emptyMessage="Nenhuma loja encontrada nesta conta."
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowAddUserDialog(false)}
            >
              Cancelar
            </Button>
            <Button onClick={handleAddUser} disabled={loading}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Adicionar Usuário
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Editar Usuário */}
      <Dialog open={showEditUserDialog} onOpenChange={setShowEditUserDialog}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editar Usuário</DialogTitle>
            <DialogDescription>
              Atualize as informações do usuário
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-user-name">Nome *</Label>
              <Input
                id="edit-user-name"
                value={userFormData.name}
                onChange={(e) =>
                  setUserFormData((prev) => ({ ...prev, name: e.target.value }))
                }
                placeholder="Nome completo"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-user-email">E-mail *</Label>
              <Input
                id="edit-user-email"
                type="email"
                value={userFormData.email}
                onChange={(e) =>
                  setUserFormData((prev) => ({
                    ...prev,
                    email: e.target.value,
                  }))
                }
                placeholder="email@exemplo.com"
              />
            </div>
            <SeletorDeNivel
              id="edit-user-access-level"
              accessType={selectedUser?.access_type ?? "team"}
              value={userFormData.accessLevel}
              onChange={(nivel) =>
                setUserFormData((prev) => ({ ...prev, accessLevel: nivel }))
              }
            />
            {/*
              O **tipo** não se troca aqui, e é de propósito: mudar de equipe para cliente é
              mudar em que conta o vínculo mora, e a API não aceita a troca. Quem se enganou
              remove e cria de novo. O que se edita é o conjunto que cabe naquele tipo.
            */}
            <div className="space-y-2">
              <Label>
                {selectedUser?.access_type === "client" ? "Lojas *" : "Times"}
              </Label>
              <p className="text-xs text-muted-foreground">
                {selectedUser?.access_type === "client"
                  ? "Credencial de cliente: enxerga, só para leitura, as lojas marcadas."
                  : "Funcionário da agência: enxerga as lojas dos times marcados."}
              </p>
              {selectedUser?.access_type === "client" ? (
                <TeamMultiSelect
                  options={opcoesDeLoja}
                  selectedIds={userFormData.accountIds}
                  onChange={(ids) =>
                    setUserFormData((prev) => ({ ...prev, accountIds: ids }))
                  }
                  loading={escopoCarregando}
                  searchPlaceholder="Buscar loja…"
                  emptyMessage="Nenhuma loja encontrada nesta conta."
                />
              ) : (
                <TeamMultiSelect
                  options={opcoesDeTime}
                  selectedIds={userFormData.teamIds}
                  onChange={(ids) =>
                    setUserFormData((prev) => ({ ...prev, teamIds: ids }))
                  }
                  loading={escopoCarregando}
                  searchPlaceholder="Buscar time…"
                  emptyMessage="Nenhum time criado ainda."
                />
              )}
            </div>
            {isCurrentUserAdmin && (
              <div className="space-y-2">
                {!showEditPassword ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setShowEditPassword(true)}
                    className="w-full"
                  >
                    <Shield className="h-4 w-4 mr-2" />
                    Alterar Senha
                  </Button>
                ) : (
                  <>
                    <Label htmlFor="edit-user-password">Nova Senha</Label>
                    <div className="relative">
                      <Input
                        id="edit-user-password"
                        type={showEditPasswordVisible ? "text" : "password"}
                        value={userFormData.password}
                        onChange={(e) =>
                          setUserFormData((prev) => ({
                            ...prev,
                            password: e.target.value,
                          }))
                        }
                        placeholder="Deixe em branco para manter a atual"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                        onClick={() =>
                          setShowEditPasswordVisible(!showEditPasswordVisible)
                        }
                      >
                        {showEditPasswordVisible ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setShowEditPassword(false);
                        setUserFormData((prev) => ({ ...prev, password: "" }));
                        setShowEditPasswordVisible(false);
                      }}
                      className="text-xs text-muted-foreground"
                    >
                      Cancelar alteração de senha
                    </Button>
                  </>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowEditUserDialog(false)}
            >
              Cancelar
            </Button>
            <Button onClick={handleEditUser} disabled={loading}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Salvar Alterações
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Confirmar Exclusão de Usuário */}
      <Dialog
        open={showDeleteUserDialog}
        onOpenChange={setShowDeleteUserDialog}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Remover Usuário
            </DialogTitle>
            <DialogDescription>
              Tem certeza que deseja remover{" "}
              <strong>{selectedUser?.name}</strong>? Esta ação não pode ser
              desfeita.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDeleteUserDialog(false)}
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteUser}
              disabled={loading}
            >
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Remover Usuário
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Detalhe da Fatura */}
      <Dialog open={showInvoiceDialog} onOpenChange={setShowInvoiceDialog}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          {invoiceDetailLoading ? (
            <div className="flex flex-col items-center justify-center py-12 gap-3">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-sm text-muted-foreground">
                Carregando fatura...
              </p>
            </div>
          ) : invoiceDetail ? (
            <div className="space-y-6">
              {/* Cabeçalho do Documento */}
              <div className="flex items-start justify-between border-b pb-4">
                <div>
                  <h2 className="text-2xl font-bold text-foreground">FATURA</h2>
                  <p className="text-sm text-muted-foreground mt-1">
                    Nº {invoiceDetail.invoice_number || "—"}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-medium text-muted-foreground">
                    Painel do E-Commerce
                  </p>
                  {(() => {
                    const statusConfig: Record<
                      string,
                      { label: string; color: string }
                    > = {
                      paid: {
                        label: "Paga",
                        color: "bg-green-100 text-green-800",
                      },
                      pending: {
                        label: "Pendente",
                        color: "bg-yellow-100 text-yellow-800",
                      },
                      overdue: {
                        label: "Vencida",
                        color: "bg-red-100 text-red-800",
                      },
                      cancelled: {
                        label: "Cancelada",
                        color: "bg-gray-100 text-gray-800",
                      },
                    };
                    const cfg =
                      statusConfig[invoiceDetail.status] ||
                      statusConfig.pending;
                    return (
                      <Badge className={`mt-2 ${cfg.color}`}>{cfg.label}</Badge>
                    );
                  })()}
                </div>
              </div>

              {/* Informações da Assinatura */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-muted/50 rounded-lg">
                <div className="space-y-3">
                  <div>
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Plano
                    </p>
                    <p className="text-sm font-semibold mt-0.5 flex items-center gap-1.5">
                      <Crown className="h-4 w-4 text-yellow-500" />
                      {invoiceDetail.plan?.name || "—"}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Ciclo de Cobrança
                    </p>
                    <p className="text-sm font-medium mt-0.5">
                      {invoiceDetail.billing_cycle || "Mensal"}
                    </p>
                  </div>
                  {invoiceDetail.subscription_start && (
                    <div>
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Data da Assinatura
                      </p>
                      <p className="text-sm font-medium mt-0.5">
                        {formatDate(invoiceDetail.subscription_start)}
                      </p>
                    </div>
                  )}
                </div>
                <div className="space-y-3">
                  <div>
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Emissão
                    </p>
                    <p className="text-sm font-medium mt-0.5">
                      {invoiceDetail.created_at
                        ? formatDate(invoiceDetail.created_at)
                        : "—"}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Vencimento
                    </p>
                    <p className="text-sm font-medium mt-0.5">
                      {invoiceDetail.due_date
                        ? formatDate(invoiceDetail.due_date)
                        : "—"}
                    </p>
                  </div>
                  {invoiceDetail.paid_at && (
                    <div>
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Pago em
                      </p>
                      <p className="text-sm font-medium mt-0.5 text-green-700">
                        {formatDate(invoiceDetail.paid_at)}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Período de Referência */}
              {(invoiceDetail.period_start || invoiceDetail.period_end) && (
                <div className="p-3 border rounded-lg">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">
                    Período de Referência
                  </p>
                  <p className="text-sm font-medium">
                    {invoiceDetail.period_start
                      ? formatDate(invoiceDetail.period_start)
                      : "—"}
                    {" — "}
                    {invoiceDetail.period_end
                      ? formatDate(invoiceDetail.period_end)
                      : "—"}
                  </p>
                </div>
              )}

              {/* Descrição */}
              {invoiceDetail.description && (
                <div className="p-3 border rounded-lg">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">
                    Descrição
                  </p>
                  <p className="text-sm">{invoiceDetail.description}</p>
                </div>
              )}

              {/* Itens da Fatura */}
              <div className="border rounded-lg overflow-hidden">
                <div className="bg-muted/70 px-4 py-2.5">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    Detalhamento
                  </p>
                </div>
                <div className="divide-y">
                  {/* Valor mensal do plano */}
                  <div className="flex items-center justify-between px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Receipt className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm">
                        Plano {invoiceDetail.plan?.name || "—"} (
                        {invoiceDetail.billing_cycle || "Mensal"})
                      </span>
                    </div>
                    <span className="text-sm font-medium">
                      {formatCurrency(
                        invoiceDetail.monthly_amount ||
                          invoiceDetail.plan?.price ||
                          0,
                      )}
                    </span>
                  </div>

                  {/* Taxa de setup */}
                  {(invoiceDetail.setup_fee ?? 0) > 0 && (
                    <div className="flex items-center justify-between px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Sparkles className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm">Taxa de Ativação</span>
                      </div>
                      <span className="text-sm font-medium">
                        {formatCurrency(invoiceDetail.setup_fee ?? 0)}
                      </span>
                    </div>
                  )}

                  {/* Itens adicionais do JSON */}
                  {invoiceDetail.items &&
                    Array.isArray(invoiceDetail.items) &&
                    invoiceDetail.items.map(
                      (item: InvoiceItem, idx: number) => (
                        <div
                          key={idx}
                          className="flex items-center justify-between px-4 py-3"
                        >
                          <span className="text-sm">
                            {item.description || item.type || `Item ${idx + 1}`}
                          </span>
                          <span className="text-sm font-medium">
                            {formatCurrency(
                              item.amount ??
                                (item.unit_price ?? 0) * (item.quantity ?? 1),
                            )}
                          </span>
                        </div>
                      ),
                    )}

                  {/* Desconto */}
                  {(invoiceDetail.discount_amount ?? 0) > 0 && (
                    <div className="flex items-center justify-between px-4 py-3 text-green-700">
                      <div className="flex items-center gap-2">
                        <Gift className="h-4 w-4" />
                        <span className="text-sm">
                          Desconto
                          {invoiceDetail.coupon_code && (
                            <span className="ml-1 text-xs bg-green-100 text-green-800 px-1.5 py-0.5 rounded">
                              {invoiceDetail.coupon_code}
                            </span>
                          )}
                        </span>
                      </div>
                      <span className="text-sm font-medium">
                        − {formatCurrency(invoiceDetail.discount_amount ?? 0)}
                      </span>
                    </div>
                  )}
                </div>

                {/* Total */}
                <div className="bg-primary/5 border-t-2 border-primary px-4 py-4 flex items-center justify-between">
                  <span className="text-base font-bold">Total a Pagar</span>
                  <span className="text-xl font-bold text-primary">
                    {formatCurrency(invoiceDetail.amount)}
                  </span>
                </div>
              </div>

              {/* Recursos do Plano */}
              {invoiceDetail.plan?.features &&
                invoiceDetail.plan.features.length > 0 && (
                  <div className="p-4 border rounded-lg bg-muted/30">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                      Recursos Incluídos no Plano
                    </p>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {invoiceDetail.plan.features.map(
                        (feature: string, idx: number) => (
                          <li
                            key={idx}
                            className="flex items-center gap-2 text-sm"
                          >
                            <CheckCircle className="h-3.5 w-3.5 text-green-500 flex-shrink-0" />
                            {feature}
                          </li>
                        ),
                      )}
                    </ul>
                  </div>
                )}

              {/* Observações */}
              {invoiceDetail.notes && (
                <div className="p-3 border rounded-lg bg-yellow-50 border-yellow-200">
                  <p className="text-xs font-medium text-yellow-800 uppercase tracking-wider mb-1">
                    Observações
                  </p>
                  <p className="text-sm text-yellow-900">
                    {invoiceDetail.notes}
                  </p>
                </div>
              )}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Dialog: Exclusão de Dados */}
      <Dialog
        open={showDataDeletionDialog}
        onOpenChange={(open) => {
          if (!deletingData) {
            setShowDataDeletionDialog(open);
            if (!open) setDeletionRequestId(null);
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          {deletionRequestId ? (
            <>
              <DialogHeader>
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-full bg-emerald-100">
                    <CheckCircle className="h-6 w-6 text-emerald-600" />
                  </div>
                  <div>
                    <DialogTitle className="text-xl">
                      Exclusão concluída
                    </DialogTitle>
                  </div>
                </div>
              </DialogHeader>
              <DialogDescription className="text-base">
                Todos os dados de integrações (Google e Meta) serão excluídos
                nos próximos minutos.
              </DialogDescription>
              <div className="p-4 bg-muted rounded-lg space-y-1">
                <p className="text-xs text-muted-foreground uppercase tracking-wider font-medium">
                  ID da solicitação
                </p>
                <p className="text-sm font-mono break-all">
                  {deletionRequestId}
                </p>
              </div>
              <DialogFooter>
                <Button
                  onClick={() => {
                    setShowDataDeletionDialog(false);
                    setDeletionRequestId(null);
                  }}
                >
                  Fechar
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <DialogHeader>
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-full bg-destructive/10">
                    <AlertTriangle className="h-6 w-6 text-destructive" />
                  </div>
                  <div>
                    <DialogTitle className="text-xl">
                      Excluir dados de integrações
                    </DialogTitle>
                  </div>
                </div>
              </DialogHeader>
              <DialogDescription className="text-base">
                Esta ação{" "}
                <strong className="text-destructive">
                  não pode ser desfeita
                </strong>
                . Todos os seus dados de integrações serão permanentemente
                excluídos:
              </DialogDescription>
              <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1">
                <li>Google Analytics</li>
                <li>Google Ads</li>
                <li>Search Console</li>
                <li>Meta Ads</li>
                <li>Instagram Insights</li>
              </ul>
              <p className="text-sm text-muted-foreground">
                Sua conta no Painel do E-Commerce permanecerá ativa.
              </p>
              <DialogFooter className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowDataDeletionDialog(false);
                    setDeletionRequestId(null);
                  }}
                  disabled={deletingData}
                >
                  Cancelar
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleDataDeletion}
                  disabled={deletingData}
                >
                  {deletingData && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  {deletingData ? "Excluindo..." : "Confirmar Exclusão"}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog: Upgrade */}
      <UpgradeDialog
        open={showUpgradeDialog}
        onOpenChange={setShowUpgradeDialog}
        currentPlanName={currentPlan?.name ?? null}
        selected={selectedEligiblePlan}
        onSuccess={() => {
          loadSubscription();
          loadEligiblePlans();
        }}
      />

      {lojaDosCreditos && (
        <BuyAiCreditsDialog
          open={buyCreditsOpen}
          onOpenChange={setBuyCreditsOpen}
          customerUuid={lojaDosCreditos}
          storeName={nomeDaLojaDosCreditos}
          onPurchased={loadAiCredits}
        />
      )}
    </div>
  );
};

export default MyAccountPage;
